import React, { useState, useEffect } from 'react';
import './Register.css';
import { 
  getActiveEvent, 
  getAvailableSpots, 
  // getAvailableTeams, // Commented out - team selection disabled
  createRegistration,
  createStripePaymentIntent
} from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '../config/stripe';
import StripePaymentForm from '../components/StripePaymentForm';

const Register = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [event, setEvent] = useState(null);
  const [availableSpots, setAvailableSpots] = useState(0);
  // const [availableTeams, setAvailableTeams] = useState([]); // Commented out - team selection disabled
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    teamName: '', 
    contactName: '',
    email: '',
    phone: '',
    players: '',
    comments: '',
  });
  const [playerNames, setPlayerNames] = useState([]);
  const [stripe, setStripe] = useState(null);
  const [phoneError, setPhoneError] = useState('');

  // Initialize Stripe
  useEffect(() => {
    const initializeStripe = async () => {
      const stripeInstance = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      setStripe(stripeInstance);
    };
    initializeStripe();
  }, []);

  // Fetch event and availability data on mount
  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Get active event
        const activeEvent = await getActiveEvent();
        setEvent(activeEvent);
        
        // Get available spots
        const spotsData = await getAvailableSpots(activeEvent.id);
        setAvailableSpots(spotsData.availableSpots || 0);
        
        // Get available teams - Commented out - team selection disabled
        // const teamsData = await getAvailableTeams(activeEvent.id);
        // setAvailableTeams(teamsData || []);
      } catch (err) {
        console.error('Error fetching event data:', err);
        setError('Failed to load registration information. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEventData();
  }, []);

  // Refresh availability when players selection changes
  useEffect(() => {
    const refreshAvailability = async () => {
      if (!event || !formData.players) return;
      
      try {
        const spotsData = await getAvailableSpots(event.id);
        setAvailableSpots(spotsData.availableSpots || 0);
        // const teamsData = await getAvailableTeams(event.id); // Commented out - team selection disabled
        // setAvailableTeams(teamsData || []);
      } catch (err) {
        console.error('Error refreshing availability:', err);
      }
    };

    refreshAvailability();
  }, [formData.players, event]);

  // Reset player names array when number of players changes
  useEffect(() => {
    const numberOfPlayers = Number(formData.players);
    if (numberOfPlayers > 1) {
      // Create array with (numberOfPlayers - 1) empty strings
      // For 2 players: 1 field, for 3 players: 2 fields, etc.
      const newPlayerNames = Array(numberOfPlayers - 1).fill('');
      setPlayerNames(newPlayerNames);
    } else {
      setPlayerNames([]);
    }
  }, [formData.players]);

  // Validate phone number format
  const validatePhoneNumber = (phone) => {
    if (!phone || phone.trim() === '') {
      return { isValid: true, error: '' }; // Optional field, empty is valid
    }
    
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    
    // Check if it's a valid US phone number (10 digits)
    // Also allow international format (11+ digits)
    if (digitsOnly.length < 10) {
      return { isValid: false, error: 'Phone number must contain at least 10 digits' };
    }
    
    if (digitsOnly.length > 15) {
      return { isValid: false, error: 'Phone number is too long' };
    }
    
    return { isValid: true, error: '' };
  };

  // Format phone number as user types
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX for US numbers
    if (digitsOnly.length <= 3) {
      return digitsOnly;
    } else if (digitsOnly.length <= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else if (digitsOnly.length <= 10) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
    } else {
      // For longer numbers (international), just add formatting
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)} ${digitsOnly.slice(10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for phone number
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      const validation = validatePhoneNumber(formatted);
      
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
      
      setPhoneError(validation.error);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handlePlayerNameChange = (index, value) => {
    setPlayerNames(prev => {
      const newNames = [...prev];
      newNames[index] = value;
      return newNames;
    });
  };

  // Handle payment submission from Stripe form
  const handleStripePayment = async (paymentMethodId) => {
    setError('');
    setSubmitting(true);
    
    try {
      // Calculate payment amount ($180 per player as per AdminPortal)
      const pricePerPlayer = 180;
      const numberOfPlayers = Number(formData.players);
      const totalAmount = numberOfPlayers * pricePerPlayer;

      // Step 1: Create payment intent WITH payment method attached
      // Backend will handle the payment intent creation and confirmation
      const paymentIntentData = {
        amount: totalAmount * 100, // Convert to cents
        currency: 'usd',
        customerEmail: formData.email,
        customerName: formData.contactName,
        description: 'Toke For Stroke Open Registration',
        paymentMethodId: paymentMethodId, // Include payment method ID for backend to attach
        captureMethod: true, // Auto-capture payment (boolean as expected by backend)
        metadata: {
          eventId: event.id.toString(),
          numberOfPlayers: numberOfPlayers.toString(),
          contactName: formData.contactName,
        }
      };

      let paymentResponse;
      try {
        paymentResponse = await createStripePaymentIntent(paymentIntentData);
      } catch (paymentErr) {
        console.error('Stripe payment intent creation error:', paymentErr);
        console.error('Request data:', paymentIntentData);
        console.error('Response:', paymentErr.response?.data);
        const paymentErrorMessage = paymentErr.response?.data?.message || 
                                    paymentErr.response?.data?.error ||
                                    paymentErr.message || 
                                    'Payment processing failed. Please try again.';
        setError(paymentErrorMessage);
        setSubmitting(false);
        return;
      }

      // Step 2: Check if backend handled confirmation or if we need to confirm client-side
      const clientSecret = paymentResponse?.clientSecret;
      const paymentIntentId = paymentResponse?.paymentIntentId;
      const status = paymentResponse?.status;

      // If backend already confirmed and payment succeeded, proceed
      if (status === 'succeeded') {
        // Payment successful, proceed with registration
      } 
      // If backend created payment intent but needs client-side confirmation (3D Secure)
      else if (clientSecret) {
        // Confirm the payment with Stripe (this handles 3D Secure automatically)
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
          clientSecret,
          {
            payment_method: paymentMethodId,
          }
        );

        if (confirmError) {
          throw new Error(confirmError.message);
        }

        // Check final payment status
        if (paymentIntent.status === 'succeeded') {
          // Payment successful, proceed with registration
        } else if (paymentIntent.status === 'requires_action') {
          throw new Error('Payment requires additional authentication. Please try again.');
        } else {
          throw new Error(`Payment failed with status: ${paymentIntent.status}`);
        }
      } 
      // If backend returned payment intent ID, check status via backend
      else if (paymentIntentId) {
        // Backend may have already processed it, check status
        if (status && status !== 'succeeded') {
          throw new Error(`Payment failed with status: ${status}`);
        }
      } 
      else {
        throw new Error('Payment intent creation failed - no client secret or payment intent ID returned');
      }

      // Step 3: If payment succeeded, create registration
      await completeRegistration(numberOfPlayers);
    } catch (err) {
      console.error('Payment processing error:', err);
      const errorMessage = err.message || 'Payment processing failed. Please try again.';
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  // Complete registration after successful payment
  const completeRegistration = async (numberOfPlayers) => {
    try {
      // Collect all player names (contact name + additional player names)
      const allPlayerNames = [formData.contactName, ...playerNames]
        .map(name => name.trim())
        .filter(name => name.length > 0);
      
      // Validate we have the correct number of player names
      if (allPlayerNames.length !== numberOfPlayers) {
        setError(`Please enter names for all ${numberOfPlayers} player${numberOfPlayers > 1 ? 's' : ''}.`);
        setSubmitting(false);
        return;
      }
      
      const registrationData = {
        eventId: event.id,
        contactName: formData.contactName,
        email: formData.email,
        phone: formData.phone || null,
        numberOfPlayers: numberOfPlayers,
        teamId: null,
        teamName: null,
        playerNames: allPlayerNames,
        comments: formData.comments || null,
      };
      
      await createRegistration(registrationData);
      setStep(3);
    } catch (err) {
      console.error('Registration error:', err);
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Registration failed. Please try again.';
      setError(errorMessage);
      
      // If spots were taken, refresh availability
      if (err.response?.status === 409 || err.response?.status === 400) {
        try {
          const spotsData = await getAvailableSpots(event.id);
          setAvailableSpots(spotsData.availableSpots || 0);
        } catch (refreshErr) {
          console.error('Error refreshing availability:', refreshErr);
        }
      }
      setSubmitting(false);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate phone number if provided
    if (formData.phone && formData.phone.trim() !== '') {
      const validation = validatePhoneNumber(formData.phone);
      if (!validation.isValid) {
        setError(validation.error);
        setPhoneError(validation.error);
        return;
      }
    }
    
    // Validate available spots
    const requestedSpots = Number(formData.players);
    if (requestedSpots > availableSpots) {
      setError(`Only ${availableSpots} spot(s) available. Please select a different number of players.`);
      return;
    }
    
    // Validate player names if more than 1 player
    if (requestedSpots > 1) {
      const missingNames = playerNames.some(name => !name.trim());
      if (missingNames) {
        setError('Please enter names for all players.');
        return;
      }
    }
    
    // Team validation - Commented out - team selection disabled
    // if (formData.teamName) {
    //   const team = availableTeams.find(t => t.name === formData.teamName);
    //   if (!team || team.availableSpots < requestedSpots) {
    //     setError('Selected team does not have enough available spots.');
    //     return;
    //   }
    // }
    // 
    // // If 2+ players, team is required
    // if (requestedSpots > 1 && !formData.teamName) {
    //   setError('Team name is required for 2 or more players.');
    //   return;
    // }
    
    setStep(2);
  };


  const renderBreadcrumb = () => {
    const handleBreadcrumbClick = (targetStep) => {
      // Only allow going back from payment to registration
      if (step === 2 && targetStep === 1) {
        setStep(1);
      }
    };

    return (
      <div className="breadcrumb">
        <span 
          className={`breadcrumb-item ${step >= 1 ? 'active' : ''} ${step === 2 ? 'clickable' : ''}`}
          onClick={() => handleBreadcrumbClick(1)}
        >
          Registration
        </span>
        {step >= 2 && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className={`breadcrumb-item ${step >= 2 ? 'active' : ''}`}>Payment</span>
          </>
        )}
        {step >= 3 && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className={`breadcrumb-item ${step >= 3 ? 'active' : ''}`}>Confirmation</span>
          </>
        )}
      </div>
    );
  };

  const getAvailablePlayerOptions = () => {
    const options = [];
    const maxPlayers = Math.min(4, availableSpots);
    
    for (let i = 1; i <= maxPlayers; i++) {
      options.push(
        <option key={i} value={i}>
          {i} Player{i > 1 ? 's' : ''} {i === 4 ? '(Foursome)' : ''}
        </option>
      );
    }
    
    return options;
  };

  const renderStep = () => {
    if (loading) {
      return (
        <div className="loading-message">
          <p>Loading registration information...</p>
        </div>
      );
    }

    if (!event) {
      return (
        <div className="error-message">
          <p>No active event found. Please contact support.</p>
        </div>
      );
    }

    if (availableSpots === 0) {
      return (
        <div className="error-message">
          <p>Sorry, this event is full. No spots are currently available.</p>
        </div>
      );
    }

    if (step === 1) {
      return (
        <form className="registration-form" onSubmit={handleRegistrationSubmit}>
          {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="availability-info" style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <strong>Available Spots: {availableSpots}</strong>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="contact-name">Contact Name</label>
              <input 
                type="text" 
                id="contact-name" 
                name="contactName" 
                placeholder="Enter contact name"
                value={formData.contactName}
                onChange={handleInputChange}
                required 
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                placeholder="Enter email address"
                value={formData.email}
                onChange={handleInputChange}
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input 
                type="tel" 
                id="phone" 
                placeholder="Phone (optional)" 
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                pattern="[\d\s\(\)\-]+"
                maxLength="20"
              />
              {phoneError && <div className="error-message" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: '#dc3545' }}>{phoneError}</div>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="players">Number of Players</label>
            <select 
              id="players" 
              name="players" 
              value={formData.players}
              onChange={handleInputChange}
              required
              disabled={availableSpots === 0}
            >
              <option value="">Select...</option>
              {getAvailablePlayerOptions()}
            </select>
          </div>
          {/* Team selection - Commented out - team selection disabled */}
          {/* <div className="form-group">
            <label htmlFor="team-name">
              Team Name {Number(formData.players) > 1 ? '' : '(Optional)'}
            </label>
            {availableTeams.length === 0 ? (
              <div className="error-message" style={{ marginTop: '0.5rem' }}>
                No teams available with enough spots. {Number(formData.players) > 1 ? 'Please select fewer players.' : 'You can register without a team.'}
              </div>
            ) : (
              <select
                id="team-name"
                name="teamName"
                value={formData.teamName}
                onChange={handleInputChange}
                required={Number(formData.players) > 1}
              >
                <option value="">{Number(formData.players) > 1 ? 'Choose a team name...' : 'No team (register as individual)'}</option>
                {availableTeams
                  .filter(team => team.availableSpots >= Number(formData.players))
                  .map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name} ({team.availableSpots} spots available)
                    </option>
                  ))}
              </select>
            )}
          </div> */}
          {Number(formData.players) > 1 && (
            <div className="form-group">
              <label>Additional Player Names</label>
              {playerNames.map((name, index) => (
                <div key={index} style={{ marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder={`Player ${index + 2} Name`}
                    value={name}
                    onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                    required
                    style={{ width: '100%' }}
                  />
                </div>
              ))}
            </div>
          )}
          <div className="form-group">
            <label htmlFor="comments">Additional Comments (Optional)</label>
            <textarea 
              id="comments" 
              name="comments" 
              placeholder="Enter any additional comments or special requests"
              rows="4"
              value={formData.comments}
              onChange={handleInputChange}
            ></textarea>
          </div>
          <button type="submit" className="submit-button" disabled={availableSpots === 0}>
            Go to Payment
          </button>
        </form>
      );
    } else if (step === 2) {
      const pricePerPlayer = 180;
      const numberOfPlayers = Number(formData.players);
      const totalAmount = numberOfPlayers * pricePerPlayer;
      const amountInCents = totalAmount * 100;

      return (
        <div className="registration-form">
          {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="payment-summary" style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span><strong>Number of Players:</strong></span>
              <span><strong>{numberOfPlayers}</strong></span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span>Price per Player:</span>
              <span>${pricePerPlayer.toFixed(2)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.5rem', borderTop: '2px solid #ddd', fontSize: '1.1em' }}>
              <span><strong>Total Amount:</strong></span>
              <span><strong>${totalAmount.toFixed(2)}</strong></span>
            </div>
          </div>
          
          {stripe ? (
            <StripePaymentForm
              amount={amountInCents}
              customerEmail={formData.email}
              customerName={formData.contactName}
              onSubmit={handleStripePayment}
              onError={(errorMsg) => setError(errorMsg)}
              submitting={submitting}
            />
          ) : (
            <div className="loading-message">
              <p>Loading payment form...</p>
            </div>
          )}
          
          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button 
              type="button" 
              className="back-button" 
              onClick={() => setStep(1)}
              disabled={submitting}
            >
              Back
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="confirmation-section">
          <div className="confirmation-icon">✓</div>
          <h2>Registration Confirmed!</h2>
          <p>Thank you for registering for {event?.name ? event.name.replace('2024', '2026') : 'the event'}.</p>
          <div className="confirmation-details">
            <p><strong>Contact:</strong> {formData.contactName}</p>
            <p><strong>Email:</strong> {formData.email}</p>
            <p><strong>Players:</strong> {formData.players}</p>
            {playerNames.length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <strong>Player Names:</strong>
                <ul style={{ marginTop: '0.25rem', paddingLeft: '1.5rem' }}>
                  <li>{formData.contactName}</li>
                  {playerNames.map((name, index) => (
                    <li key={index}>{name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <p className="confirmation-message">You will receive a confirmation email shortly.</p>
        </div>
      );
    }
  };

  const getHeading = () => {
    if (step === 1) return 'Registration';
    if (step === 2) return 'Payment';
    return 'Confirmation';
  };

  return (
    <div className="register-page">
      <div className="register-container">
        <div className="breadcrumb-container">
          {renderBreadcrumb()}
        </div>
        <h1>{getHeading()}</h1>
        <div className="registration-form-container">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default Register;

